import { useEffect, useCallback, useRef } from 'react';

// 히스토리 스택 관리 (전역) - 각 오버레이 컴포넌트(모달, 드로어 등)의 콜백 함수를 저장
interface HistoryStackItem {
  pageName: string;
  id: symbol;
  handleGoBack: () => void;
  modalIdRef: React.MutableRefObject<symbol | null>; // 컴포넌트가 실제로 열려있는지 확인하기 위한 ref
}

const historyStack: HistoryStackItem[] = [];

// 전역 popstate 핸들러 (최상위 컴포넌트만 처리)
let globalPopStateHandler: ((event: PopStateEvent) => void) | null = null;

const useBackHistory = ({ isOpen, onPrev, pageName }: { isOpen: boolean; onPrev: () => void; pageName: string }): { onBackPrev: () => void } => {
  const isBackButtonClicked = useRef(false);
  const modalIdRef = useRef<symbol | null>(null);
  const onPrevRef = useRef(onPrev);
  const isProgrammaticClose = useRef(false); // 프로그래밍 방식으로 닫힌 경우 플래그

  // onPrev를 래핑하여 스택에서 제거하는 함수
  const removeFromStackAndPrev = useCallback(() => {
    // 컴포넌트가 닫힐 때 스택에서 제거
    if (modalIdRef.current) {
      const index = historyStack.findIndex((item) => item.id === modalIdRef.current);
      const wasTopItem = index !== -1 && index === historyStack.length - 1;

      if (index !== -1) {
        historyStack.splice(index, 1);
        // console.log('[useBackHistory] 스택에서 제거 (onPrev)', { pageName, index, remainingStackLength: historyStack.length });
      }

      // 전역 핸들러 업데이트 (현재 컴포넌트가 최상위였던 경우)
      if (wasTopItem) {
        // history.back() 호출 전에 기존 핸들러 제거
        if (globalPopStateHandler) {
          window.removeEventListener('popstate', globalPopStateHandler);
          globalPopStateHandler = null;
        }

        // 뒤로가기로 닫힌 게 아니고, 현재 컴포넌트가 최상위였던 경우에만 history.back() 실행
        if (!isBackButtonClicked.current) {
          isProgrammaticClose.current = true;
          window.history.back();
          // history.back() 호출 후 popstate 이벤트가 발생하므로,
          // 다음 이벤트 루프에서 다음 컴포넌트의 핸들러를 등록
          setTimeout(() => {
            isProgrammaticClose.current = false;
            // 다음 최상위 컴포넌트가 있으면 핸들러 재등록
            if (historyStack.length > 0) {
              globalPopStateHandler = (event: PopStateEvent) => {
                // 프로그래밍 방식으로 닫힌 경우 무시
                if (isProgrammaticClose.current) {
                  // console.log('[useBackHistory] popstate 이벤트 무시 (프로그래밍 방식 닫기 - 재등록된 핸들러)', { historyStackLength: historyStack.length });
                  isProgrammaticClose.current = false;
                  return;
                }
                if (historyStack.length > 0) {
                  const topItem = historyStack[historyStack.length - 1];
                  topItem.handleGoBack();
                }
              };
              window.addEventListener('popstate', globalPopStateHandler);
            }
          }, 0);
        } else if (historyStack.length > 0) {
          // 뒤로가기로 닫힌 경우, 다음 최상위 컴포넌트가 있으면 핸들러 재등록
          globalPopStateHandler = (event: PopStateEvent) => {
            if (historyStack.length > 0) {
              const topItem = historyStack[historyStack.length - 1];
              topItem.handleGoBack();
            }
          };
          window.addEventListener('popstate', globalPopStateHandler);
        }
      }

      modalIdRef.current = null;
    }

    // 원본 onPrev 호출
    onPrevRef.current();
  }, [pageName]);

  // onPrev를 항상 최신 값으로 유지
  useEffect(() => {
    onPrevRef.current = onPrev;
  }, [onPrev]);

  useEffect(() => {
    if (!isOpen) {
      // 컴포넌트가 닫힐 때 처리
      // console.log('[useBackHistory] 컴포넌트 닫힘 처리 시작', { pageName, hasModalId: !!modalIdRef.current });
      // removeFromStackAndPrev에서 이미 스택 제거 및 history.back() 처리를 했으므로
      // 여기서는 추가 처리 없이 정리만 수행
      // 하지만 onPrev가 직접 호출된 경우를 대비해 여기서도 스택에서 제거
      if (modalIdRef.current) {
        const index = historyStack.findIndex((item) => item.id === modalIdRef.current);
        if (index !== -1) {
          historyStack.splice(index, 1);
          // console.log('[useBackHistory] 스택에서 제거 (!isOpen)', { pageName, index, remainingStackLength: historyStack.length });
        }

        // 전역 핸들러 업데이트
        const wasTopItem = index !== -1 && index === historyStack.length;
        if (wasTopItem) {
          if (globalPopStateHandler) {
            window.removeEventListener('popstate', globalPopStateHandler);
          }

          if (historyStack.length > 0) {
            globalPopStateHandler = (event: PopStateEvent) => {
              if (historyStack.length > 0) {
                const topItem = historyStack[historyStack.length - 1];
                topItem.handleGoBack();
              }
            };
            window.addEventListener('popstate', globalPopStateHandler);
          } else {
            globalPopStateHandler = null;
          }
        }

        // 뒤로가기로 닫힌 게 아니고, 현재 컴포넌트가 최상위였던 경우에만 history.back() 실행
        if (!isBackButtonClicked.current && wasTopItem) {
          isProgrammaticClose.current = true;
          window.history.back();
        }

        isBackButtonClicked.current = false;
        modalIdRef.current = null;
      }
      return undefined;
    }

    // 컴포넌트 고유 ID 생성 또는 기존 ID 사용
    let itemId: symbol;
    if (modalIdRef.current) {
      // 이미 컴포넌트가 스택에 있는 경우 (의존성 변경으로 재실행)
      itemId = modalIdRef.current;
      // 기존 항목을 찾아서 콜백만 업데이트
      const existingIndex = historyStack.findIndex((item) => item.id === itemId);
      if (existingIndex !== -1) {
        // 기존 항목의 콜백만 업데이트
        // console.log('[useBackHistory] 기존 컴포넌트 콜백 업데이트', { pageName, index: existingIndex });
      }
    } else {
      // 새로운 컴포넌트
      itemId = Symbol(pageName);
      modalIdRef.current = itemId;
    }

    // handleGoBack 함수 생성 (effect 내부에서 생성하여 의존성 문제 해결)
    const handleGoBack = () => {
      // popstate 이벤트가 발생했을 때는 항상 최상위 컴포넌트이므로
      // 현재 컴포넌트가 최상위인지 확인 후 닫기
      if (historyStack.length > 0) {
        const topItem = historyStack[historyStack.length - 1];
        // 최상위 컴포넌트가 실제로 열려있는지 확인
        const isTopItemOpen = topItem.modalIdRef.current !== null;

        if (!isTopItemOpen) {
          // 이미 닫혀있는 컴포넌트 (닫기 버튼으로 닫힌 경우) - 스택에서만 제거
          const index = historyStack.findIndex((item) => item.id === topItem.id);
          const wasTopItem = index !== -1 && index === historyStack.length - 1;
          if (index !== -1) {
            historyStack.splice(index, 1);
            // console.log('[useBackHistory] 이미 닫혀있는 컴포넌트 스택에서 제거', { pageName: topItem.pageName, index, remainingStackLength: historyStack.length });
          }

          // 전역 핸들러 업데이트
          if (wasTopItem) {
            if (historyStack.length === 0) {
              if (globalPopStateHandler) {
                window.removeEventListener('popstate', globalPopStateHandler);
                globalPopStateHandler = null;
              }
            } else {
              // 다음 최상위 컴포넌트가 있으면 핸들러 재등록
              if (globalPopStateHandler) {
                window.removeEventListener('popstate', globalPopStateHandler);
              }
              globalPopStateHandler = (event: PopStateEvent) => {
                if (historyStack.length > 0) {
                  const nextTopItem = historyStack[historyStack.length - 1];
                  nextTopItem.handleGoBack();
                }
              };
              window.addEventListener('popstate', globalPopStateHandler);
              // 다음 최상위 컴포넌트의 handleGoBack을 즉시 호출하여 처리
              if (historyStack.length > 0) {
                const nextTopItem = historyStack[historyStack.length - 1];
                // console.log('[useBackHistory] 다음 최상위 컴포넌트 handleGoBack 호출', { pageName: nextTopItem.pageName });
                nextTopItem.handleGoBack();
              }
            }
          }
          return;
        }

        // 현재 컴포넌트가 최상위인 경우에만 닫기
        if (modalIdRef.current && topItem.id === modalIdRef.current) {
          // console.log('[useBackHistory] 컴포넌트 닫기 실행 (popstate)', { pageName });
          isBackButtonClicked.current = true;
          // 래핑된 onPrev 호출 (스택에서 제거 포함)
          removeFromStackAndPrev();
        }
      }
    };

    // 히스토리 스택에 추가 또는 업데이트
    const existingIndex = historyStack.findIndex((item) => item.id === itemId);
    const isNewItem = existingIndex === -1;

    if (existingIndex !== -1) {
      // 기존 항목 업데이트
      historyStack[existingIndex] = { pageName, id: itemId, handleGoBack, modalIdRef };
    } else {
      // 새 항목 추가
      historyStack.push({ pageName, id: itemId, handleGoBack, modalIdRef });
    }

    // 새 항목인 경우에만 pushState 실행 (중복 방지)
    if (isNewItem) {
      window.history.pushState({ page: pageName }, document.title);
    }

    // 전역 popstate 핸들러 설정 (최상위 컴포넌트만 처리)
    // 기존 핸들러가 있으면 제거하고 새로 등록
    if (globalPopStateHandler) {
      window.removeEventListener('popstate', globalPopStateHandler);
    }

    globalPopStateHandler = (event: PopStateEvent) => {
      // 프로그래밍 방식으로 닫힌 경우 (history.back() 호출로 인한 popstate) 무시
      if (isProgrammaticClose.current) {
        // console.log('[useBackHistory] popstate 이벤트 무시 (프로그래밍 방식 닫기)', { historyStackLength: historyStack.length });
        isProgrammaticClose.current = false;
        return;
      }

      // 최상위 컴포넌트의 handleGoBack 호출
      // popstate 이벤트는 이미 history가 변경된 후에 발생하므로,
      // 최상위 컴포넌트를 닫아야 함
      // console.log('[useBackHistory] popstate 이벤트 발생', { historyStackLength: historyStack.length, eventState: event.state });
      if (historyStack.length > 0) {
        const topItem = historyStack[historyStack.length - 1];
        // console.log('[useBackHistory] 최상위 컴포넌트 handleGoBack 호출', { pageName: topItem.pageName });
        // 최상위 컴포넌트의 handleGoBack을 직접 호출
        topItem.handleGoBack();
      }
    };

    window.addEventListener('popstate', globalPopStateHandler);

    return () => {
      // cleanup: effect가 다시 실행되기 전에 실행됨
      // console.log('[useBackHistory] cleanup 실행', { pageName, hasModalId: !!modalIdRef.current, isOpen });
      // cleanup이 실행될 때는 다음 effect가 실행되기 전이므로,
      // 다음 effect의 isOpen 값을 알 수 없음
      // 따라서 cleanup에서는 스택을 제거하지 않고,
      // 다음 effect 실행 시 !isOpen 블록에서 처리하도록 함
      // 하지만 컴포넌트가 닫히는 중이라면 스택에서 제거해야 함
      // cleanup이 실행되는 경우:
      // 1. 의존성 변경으로 재실행 (isOpen이 여전히 true)
      // 2. 컴포넌트가 닫힘 (isOpen이 false로 변경됨)
      // cleanup에서는 스택을 제거하지 않음
      // 실제로 닫힐 때는 다음 effect 실행 시 !isOpen 블록에서 처리됨
    };
  }, [isOpen, pageName, removeFromStackAndPrev]);

  // 래핑된 onPrev 반환
  return { onBackPrev: removeFromStackAndPrev };
};

export default useBackHistory;
